import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { runOptimizer, getOptimizerLatest, formatApiError } from "@/lib/api";

const BOTTLENECK = { creativita: "Creatività", target: "Target", offerta: "Offerta", landing: "Landing", budget: "Budget", conversione: "Conversione", follow_up: "Follow-up", dati_insufficienti: "Dati insufficienti", nessuno: "Nessuno" };

function List({ title, items, cls = "text-slate-200", testId }) {
  if (!items?.length) return null;
  return (
    <div data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">{title}</div>
      <ul className="space-y-1 text-sm">{items.map((x, i) => <li key={i} className={cls}>· {typeof x === "string" ? x : x.title || JSON.stringify(x)}</li>)}</ul>
    </div>
  );
}

export function OptimizerPanel() {
  const [a, setA] = useState(null);
  const [at, setAt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getOptimizerLatest().then((r) => { setA(r.analysis); setAt(r.at); }).catch(() => {}); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const r = await runOptimizer();
      setA(r); setAt(new Date().toISOString());
      toast.success(r.actions_created?.length ? `${r.actions_created.length} proposte in attesa della tua approvazione (Azioni Nadia)` : "Analisi completata");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="nadia-card p-6" data-testid="nadia-insight-panel">
      <div className="flex items-start gap-4">
        <NadiaAvatar size="md" />
        <div className="flex-1 min-w-0 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wider">Analisi di Nadia sui dati reali</div>
            <Button size="sm" variant="outline" onClick={run} disabled={busy} className="border-white/10" data-testid="optimizer-run">
              {busy ? <Loader2 size={13} className="animate-spin mr-1" /> : <Sparkles size={13} className="mr-1" />} Analizza ora
            </Button>
          </div>
          {!a ? (
            <p className="text-sm text-slate-400" data-testid="optimizer-empty">Nessuna analisi ancora. Nadia legge solo metriche reali delle campagne pubblicate: senza dati non produce stime.</p>
          ) : (
            <>
              <p className="text-slate-100 text-sm sm:text-base leading-relaxed" data-testid="optimizer-summary">{a.summary}</p>
              <div className="flex flex-wrap gap-2 text-[11px]">
                <span className={`px-2 py-0.5 rounded-full border ${a.data_sufficient ? "border-emerald-500/30 text-emerald-300" : "border-amber-500/30 text-amber-300"}`} data-testid="optimizer-data-flag">{a.data_sufficient ? "Dati sufficienti" : "Dati insufficienti"}</span>
                <span className="px-2 py-0.5 rounded-full border border-white/10 text-slate-300" data-testid="optimizer-bottleneck">Collo di bottiglia: {BOTTLENECK[a.suspected_bottleneck] || a.suspected_bottleneck}</span>
                {at && <span className="px-2 py-0.5 text-slate-500">{new Date(at).toLocaleString("it-IT")}</span>}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <List title="Cosa funziona" items={a.working} cls="text-emerald-200" testId="optimizer-working" />
                <List title="Cosa non funziona" items={a.not_working} cls="text-rose-200" testId="optimizer-not-working" />
                <List title="Ipotesi" items={a.hypotheses} testId="optimizer-hypotheses" />
                <List title="Prossimi test" items={a.next_tests} testId="optimizer-tests" />
              </div>
              {a.proposed_actions?.length > 0 && (
                <div data-testid="optimizer-actions">
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-1">Azioni proposte {a.proposed_actions.some((x) => x.requires_approval) && <span className="text-amber-300 normal-case">· richiedono la tua approvazione</span>}</div>
                  <ul className="space-y-2">{a.proposed_actions.map((x, i) => (
                    <li key={i} className="text-sm text-slate-200"><span className="font-semibold text-white">{x.title}</span> — {x.reason} <span className="text-slate-500">({x.risk}{x.requires_approval ? " · approvazione" : ""})</span></li>
                  ))}</ul>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
